/* eslint-disable complexity */
import { MiddlewarePaymentError } from '../../error/MiddlewarePaymentError.js';
import type {
  InteractionErrorProps,
  InteractionPrePaymentMiddlewareFn,
  InteractionSuccessProps,
  OnInteraction,
} from '../../flow/types.js';
import type { PreMiddlewareFn } from '../../payment/createPayment.js';
import { fetchMarketplaceBalance } from '../../utils/entry.js';
import { logger } from '../../utils/index.js';
import type { Wager } from '../../wager/Wager.js';
import { recalculateRetailBalance } from './helpers/recount-retail-balance.js';

type BalanceSuccessInteraction = InteractionSuccessProps & {
  insufficientWallets: { balance: number; name: string; price: number }[];
};
type BalanceErrorInteraction = InteractionErrorProps & { title: string; message: string[] };

/**
 * Payment done middleware interaction props.
 *
 * @param onInteraction The callback to trigger the interaction.
 * @returns The middleware function.
 */
export function marketplaceBalanceMw(
  onInteraction: OnInteraction<BalanceSuccessInteraction, BalanceErrorInteraction>
): PreMiddlewareFn<Wager> {
  const fn: ReturnType<InteractionPrePaymentMiddlewareFn<Wager>> =
    ({ payment }) =>
    async (next) => {
      try {
        let shouldShowDialog = false;
        const insufficientWallets: { balance: number; name: string; price: number }[] = [];
        for (const payable of payment.getPayableItems()) {
          const { activatedDrawId, price } = payable;
          if (!activatedDrawId) {
            continue;
          }

          const { bets, customer, commissionPercent } = payable;
          const [bet] = bets;

          // eslint-disable-next-line no-await-in-loop
          const balanceResponse = await fetchMarketplaceBalance({
            activatedDrawId,
            product: bet?.product,
            drawNumber: bet?.drawNumber,
            customer,
            shouldForceFetch: false,
          });

          let balance = 0;
          if (customer === 'Retailer' && commissionPercent !== undefined && balanceResponse) {
            balance = recalculateRetailBalance(
              balanceResponse.soldShares,
              balanceResponse.sharePrice,
              commissionPercent
            );
          } else {
            balance = balanceResponse ? balanceResponse.balance : 0;
          }

          // common.logger.info(`Checking marketplace activatedDraw balance: ${balance}, price: ${price}`);

          if (price > balance) {
            shouldShowDialog = true;
            insufficientWallets.push({ balance, name: balanceResponse?.name ?? 'ni', price });
          }
        }
        if (shouldShowDialog) {
          const wagerCountText = insufficientWallets.length > 1 ? 'de här spelen' : 'det här spelet';
          const title = `Ni har inte tillräckligt med pengar för att betala ${wagerCountText}.`;

          const interactionProps = {
            title,
            insufficientWallets,
          };

          // common.logger.info('Showing dialog for not enough money MP.');

          await onInteraction(interactionProps);

          const mwError = new MiddlewarePaymentError({
            clientCode: 'NOT_ENOUGH_MONEY',
          });
          next(mwError);
          return;
        }
        next();
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const err = error instanceof Error ? error : new Error(`${error}`);

        logger.info('Error occurred', err.message);

        const mwError = new MiddlewarePaymentError({ clientCode: 'NOT_ENOUGH_MONEY' });
        next(mwError);
      }
    };
  return fn;
}
